import React from "react";
import { Plus, Save } from "lucide-react";
import SearchableSelect from "../SearchableSelect";
import { Button } from "../ui/button";
import { PassiveBadge } from "../ui/data-presentation";

const sourceLabel = (row) => row?.source_type === "cannibal" ? "Cannibal" : "Inventory";

function SourceBadge({ source }) {
  const cannibal = source === "Cannibal";
  return (
    <PassiveBadge
      tone={cannibal ? "warning" : "info"}
      data-testid={`used-source-${source.toLowerCase()}`}
    >
      {source}
    </PassiveBadge>
  );
}

export default function UnifiedUsedPartPanel({
  inventoryRows = [],
  inventoryRowId,
  editMode = false,
  selectedInventoryIds = [],
  onToggleInventory,
  onRemoveInventory,
  inventorySelectable,
  cannibalLedger,
  stateMessage = "",
}) {
  const cannibalRows = cannibalLedger?.rows || [];
  const selectedCannibalIds = cannibalLedger?.selectedIds || [];
  const hasSelection = selectedInventoryIds.length > 0 || selectedCannibalIds.length > 0;
  const columns = editMode
    ? "grid-cols-[44px_minmax(120px,0.75fr)_minmax(200px,1.25fr)]"
    : "grid-cols-[minmax(120px,0.75fr)_minmax(200px,1.25fr)]";

  const removeSelected = () => {
    if (selectedInventoryIds.length) onRemoveInventory?.();
    if (selectedCannibalIds.length) cannibalLedger?.removeSelected?.();
  };

  const renderRow = (row, id, source, checked, onToggle, disabled) => (
    <div
      key={`${source}:${id}`}
      className={`grid ${columns} items-start border-b border-zinc-100 px-3 py-3 text-sm last:border-b-0 dark:border-zinc-800`}
      data-testid={`unified-used-row-${source.toLowerCase()}-${id}`}
      data-preview-id={source === "Inventory" ? id : undefined}
    >
      {editMode ? (
        <div>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle?.(id)}
            disabled={disabled}
            className="h-5 w-5 rounded-sm border-zinc-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Select ${source} used part ${row.part_number || id}`}
          />
        </div>
      ) : null}
      <div className="text-xs font-semibold tracking-[0.02em] text-zinc-700 dark:text-zinc-300">
        {row.model || "-"}
      </div>
      <div className="min-w-0">
        <div className="font-medium text-zinc-900 dark:text-zinc-100">
          {row.part_name || "Unnamed spare part"}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <span>{row.part_number || "-"}</span>
          <SourceBadge source={source} />
        </div>
      </div>
    </div>
  );

  return (
    <section
      className="overflow-hidden rounded-sm border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950"
      data-testid="unified-used-part"
      data-transfer-panel="used"
    >
      <div className={`grid ${columns} items-center border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400`}>
        {editMode ? <div /> : null}
        <div>Model</div>
        <div className="flex items-center justify-between gap-3">
          <span>Used Part</span>
          {editMode ? (
            <Button
              type="button"
              variant="transfer-remove"
              size="sm"
              onClick={removeSelected}
              disabled={!hasSelection}
              data-action-category="transfer-remove"
            >
              Remove
            </Button>
          ) : null}
        </div>
      </div>

      <div className="max-h-[300px] overflow-y-auto" data-testid="unified-used-rows">
        {stateMessage ? (
          <div className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">{stateMessage}</div>
        ) : inventoryRows.length || cannibalRows.length ? (
          <>
            {inventoryRows.map((row) => {
              const id = inventoryRowId(row);
              return renderRow(
                row,
                id,
                sourceLabel(row),
                selectedInventoryIds.includes(id),
                onToggleInventory,
                !onToggleInventory || (inventorySelectable ? !inventorySelectable(row) : false)
              );
            })}
            {cannibalRows.map((row) => renderRow(
              row,
              row.key,
              "Cannibal",
              selectedCannibalIds.includes(row.key),
              cannibalLedger?.toggleSelection,
              !cannibalLedger?.canRemove
            ))}
          </>
        ) : (
          <div className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">No used part records.</div>
        )}
      </div>

      {cannibalLedger?.canAdd ? (
        <div className="border-t border-zinc-200 p-3 dark:border-zinc-800">
          {cannibalLedger.adding ? (
            <div className="grid grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)_52px] items-stretch overflow-hidden rounded-[var(--segmented-radius-container)] border border-success" data-testid="cannibal-inline-add">
              <SearchableSelect
                value={cannibalLedger.draftModel}
                onChange={cannibalLedger.setDraftModel}
                options={cannibalLedger.modelOptions}
                placeholder="Select Model"
                disabled={cannibalLedger.disabled}
                testid="cannibal-inline-model"
                allowClear
                portal
                containerClassName="min-w-0 border-r border-success"
                triggerClassName="h-12 !rounded-none !border-0"
              />
              <SearchableSelect
                value={cannibalLedger.draftItemId}
                onChange={cannibalLedger.setDraftItemId}
                options={cannibalLedger.itemOptions}
                placeholder={cannibalLedger.draftModel ? "Select Spare Part" : "Select Model first"}
                disabled={cannibalLedger.disabled || !cannibalLedger.draftModel}
                testid="cannibal-inline-item"
                allowClear
                portal
                containerClassName="min-w-0 border-r border-success"
                triggerClassName="h-12 !rounded-none !border-0"
              />
              <Button
                type="button"
                variant="transfer-add"
                size="icon"
                className="h-12 w-full !rounded-none !border-0 shadow-none [&_svg]:!size-6"
                onClick={cannibalLedger.saveDraft}
                disabled={cannibalLedger.disabled || !cannibalLedger.draftModel || !cannibalLedger.draftItemId}
                aria-label="Save Cannibal part to local draft"
                data-testid="cannibal-inline-save"
              >
                <Save className="h-6 w-6" />
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="transfer-add"
              className="w-full"
              onClick={cannibalLedger.startAdd}
              disabled={cannibalLedger.disabled}
              data-testid="add-cannibal-part"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Cannibal Part
            </Button>
          )}
        </div>
      ) : cannibalLedger?.transferred ? (
        <div className="border-t border-warning/30 bg-warning/10 px-3 py-2 text-xs font-medium text-warning" role="status" data-testid="transferred-donor-warning">
          Transferred donor — no new Cannibal parts.
        </div>
      ) : null}
    </section>
  );
}
